import React, { useCallback, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

import { usePendingSelfEnroll } from '../../courses/hooks';
import { usePendingPartnerClaim } from '../../partners/hooks';
import { usePendingInviteLinkClaim } from '../hooks';
import type { ClaimInviteLinkResponse } from '../endpoints';

/**
 * Runs every pending post-auth action — invite-link seat claim, self-enroll,
 * partner seat — and tells the learner how it went.
 *
 * Mounted once in App.tsx rather than on the dashboard, for two reasons:
 *
 *   1. Coverage. Auth0 returns to window.location.origin, and LandingPage then
 *      fans out to /admin/dashboard, a stored returnPath, or /welcome/:slug as
 *      well as /dashboard. Hanging these off DashboardPage meant every one of
 *      those landings silently skipped them.
 *   2. Visibility. A failed claim used to produce nothing at all — no course on
 *      the dashboard and no message — which reads to the learner exactly like a
 *      broken invite link, and gives support nothing to work with.
 *
 * This must stay mounted unconditionally. Wrapping it in a route or chrome
 * condition (the way ProfileCompletionBanner beside it is) would silently
 * reintroduce (1).
 */
type Success =
  | { kind: 'inviteLink'; result: ClaimInviteLinkResponse }
  | { kind: 'course' }
  | null;

export default function InviteClaimBanner() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<Success>(null);

  // Stable identities: the hooks list these as effect deps, so inline closures
  // would re-run the effect on every render.
  const handleClaimed = useCallback((result: ClaimInviteLinkResponse) => {
    // A repeat claim is a no-op server-side; saying "you're enrolled" again on
    // every visit would be noise.
    if (!result.already_redeemed) setSuccess({ kind: 'inviteLink', result });
  }, []);
  // Several actions can legitimately be queued at once (two tabs, one signed-in
  // session). The named seat is the more specific news, so it wins.
  const handleCourseAdded = useCallback(() => {
    setSuccess((prev) => (prev?.kind === 'inviteLink' ? prev : { kind: 'course' }));
  }, []);
  const handleError = useCallback((message: string) => setError(message), []);

  usePendingInviteLinkClaim(handleClaimed, handleError);
  usePendingSelfEnroll(handleCourseAdded, handleError);
  usePendingPartnerClaim(handleCourseAdded, handleError);

  let successText = '';
  if (success?.kind === 'inviteLink') {
    successText = success.result.kind === 'event' && success.result.event
      ? `You're registered for ${success.result.event.display_title}.`
      : `${success.result.course.name} has been added to your dashboard.`;
  } else if (success?.kind === 'course') {
    successText = 'Your course has been added to your dashboard.';
  }

  return (
    <>
      <Snackbar
        open={!!success}
        autoHideDuration={8000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess(null)}>
          {successText}
        </Alert>
      </Snackbar>

      {/* Errors do not auto-hide: this is the only place the learner is ever
          told their seat was not claimed, and it is the sentence they will
          quote back to their administrator. */}
      <Snackbar
        open={!!error}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ maxWidth: 560 }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
